import { fixedUi } from '@/i18n/fixedUi';
import {
  enrichTemuSizeChartClass,
  temuSizeChartClassName,
} from '@/temu-platform/utils/temuSizeChartClassCatalog';

/**
 * Temu 官方尺码表（bg.goods.sizecharts.*）工具。
 * 必填判定：sizecharts.class.get 有 sizeSpecClassCat.classId 即为必填。
 */

export function asSizeChartList(v) {
  if (Array.isArray(v)) return v;
  if (!v || typeof v !== 'object') return [];
  if (Array.isArray(v.items)) return v.items;
  if (Array.isArray(v.content)) return v.content;
  if (Array.isArray(v.sizeSpecDataList)) return v.sizeSpecDataList;
  if (Array.isArray(v.list)) return v.list;
  return [];
}

export function parseSizeChartClass(res) {
  const cls = res?.sizeSpecClassCat || res?.result?.sizeSpecClassCat || null;
  if (!cls || !(Number(cls.classId) > 0)) {
    return { required: false, sizeClass: null };
  }
  return { required: true, sizeClass: enrichTemuSizeChartClass(cls) };
}

/** 列表行展示：优先 API className，否则按 classId 映射 */
export function formatSizeChartClassLabel(row) {
  const classId = Number(row?.classId || row?.sizeClass?.classId || 0);
  const named = String(row?.className || row?.sizeClass?.className || '').trim();
  if (named && !/^\d+$/.test(named)) return fixedUi(named);
  const mapped = temuSizeChartClassName(classId);
  return mapped ? fixedUi(mapped) : '';
}

export function pickSizeMetaLists(metaRes, settingsRes) {
  const groupList = metaRes?.sizeSpecMeta?.groupList
    || settingsRes?.mappingContent?.meta?.groupList
    || [{ id: 1, name: '尺码' }];
  const elementList = metaRes?.sizeSpecMeta?.elementList
    || settingsRes?.mappingContent?.meta?.elementList
    || [];
  return { groupList, elementList };
}

/** 从一行 values 里取出尺码名（兼容 groupId / 1 / sizeName 等历史键） */
export function extractSizeNameFromRecordValues(values, groupList = []) {
  if (!values || typeof values !== 'object') return '';
  const groupId = String(groupList[0]?.id ?? 1);
  const candidates = [
    values[groupId],
    values[String(groupId)],
    values['1'],
    values[1],
    values.sizeName,
    values.groupId,
    values.name,
  ];
  for (const raw of candidates) {
    const text = String(raw ?? '').trim();
    if (text) return text;
  }
  return '';
}

export function buildSizeChartRecords({
  groupList = [],
  elementList = [],
  sizeNames = [],
  prevRecords = [],
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const names = (Array.isArray(sizeNames) ? sizeNames : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  // 行数严格跟随主规格已选尺码，不自动补 S/M/L
  const prevBySize = new Map();
  (Array.isArray(prevRecords) ? prevRecords : []).forEach((row) => {
    const values = row?.values && typeof row.values === 'object' ? row.values : {};
    const key = extractSizeNameFromRecordValues(values, groupList);
    if (!key) return;
    prevBySize.set(key, values);
    prevBySize.set(key.toLowerCase(), values);
  });
  return names.map((sizeName) => {
    const prev = prevBySize.get(String(sizeName))
      || prevBySize.get(String(sizeName).toLowerCase())
      || {};
    const values = { [groupId]: String(sizeName) };
    const elementIds = new Set();
    (elementList || []).forEach((el) => {
      const id = String(el.id);
      elementIds.add(id);
      // 兼容历史：测量值可能写在数字键或字符串键上
      const raw = prev[id] != null ? prev[id] : prev[el.id];
      values[id] = raw != null ? String(raw) : '';
    });
    // elementList 尚未还原时，先把旧测量键原样带上，避免编辑页整表变空
    if (!(elementList || []).length) {
      Object.keys(prev).forEach((key) => {
        const k = String(key);
        if (k === groupId || k === '1' || k === 'sizeName' || k === 'groupId' || k === 'name') return;
        if (values[k] != null && String(values[k]).trim() !== '') return;
        const raw = prev[key];
        if (raw == null || String(raw).trim() === '') return;
        values[k] = String(raw);
      });
    } else {
      // 有 elementList 时也兜底：prev 里同名键的别名（数字/字符串）
      Object.keys(prev).forEach((key) => {
        const k = String(key);
        if (elementIds.has(k)) return;
        if (k === groupId || k === '1' || k === 'sizeName' || k === 'groupId' || k === 'name') return;
      });
    }
    return { values };
  });
}

/**
 * records 是否仍是用户正在编辑的尺码行（有尺码名）。
 * 测量格被清空时仍算编辑中，不能当成加载中的空壳回填旧值。
 */
export function officialSizeChartRecordsHaveSizeNames(records, groupList = []) {
  return (Array.isArray(records) ? records : []).some((row) => {
    const values = row?.values && typeof row.values === 'object' ? row.values : row;
    return !!extractSizeNameFromRecordValues(values, groupList);
  });
}

/** records 是否含任意测量值（排除尺码名 / 分组壳字段） */
export function officialSizeChartRecordsHaveValues(records, groupList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const skip = new Set([groupId, '1', 'sizeName', 'groupId', 'name']);
  return (Array.isArray(records) ? records : []).some((row) => {
    const values = row?.values && typeof row.values === 'object' ? row.values : row;
    if (!values || typeof values !== 'object') return false;
    return Object.keys(values).some((key) => {
      if (skip.has(String(key))) return false;
      const val = values[key];
      return val != null && String(val).trim() !== '';
    });
  });
}

/** 草稿是否值得回填（有测量值或已选库表） */
export function officialSizeChartDraftHasContent(draft, groupList = []) {
  if (!draft || typeof draft !== 'object') return false;
  if (Array.isArray(draft.selectedBusinessIds) && draft.selectedBusinessIds.some((id) => Number(id) > 0)) {
    return true;
  }
  if (String(draft.selectedLibraryId || '').trim()) return true;
  return officialSizeChartRecordsHaveValues(draft.records, groupList);
}

export function emptySizeChartRecord(groupList = [], elementList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const values = { [groupId]: '' };
  (elementList || []).forEach((el) => {
    values[String(el.id)] = '';
  });
  return { values };
}

/** 提交前去掉空字符串测量值（Temu create 遇空值会报 empty size value） */
export function sanitizeSizeChartRecordValues(values = {}, groupList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const next = {};
  Object.entries(values && typeof values === 'object' ? values : {}).forEach(([key, raw]) => {
    const k = String(key);
    if (k === 'sizeName' || k === 'groupId' || k === 'name') return;
    const val = raw == null ? '' : String(raw).trim();
    if (!val) return;
    next[k] = val;
  });
  // 尺码名必须保留；若缺失则无法提交
  if (!next[groupId] && values?.[groupId] != null) {
    const sizeName = String(values[groupId]).trim();
    if (sizeName) next[groupId] = sizeName;
  }
  return next;
}

/**
 * 官方样例：meta.elementList 只声明实际有值的测量列。
 * 若把空列（腰围/领围等）也放进 elementList，平台会按声明列校验并拒单。
 * @returns {{ ok: true, elementList: Array, records: Array } | { ok: false, message: string }}
 */
export function pickFilledSizeChartElements({
  records = [],
  groupList = [],
  elementList = [],
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const sanitizedRecords = (records || []).map((r) => ({
    values: sanitizeSizeChartRecordValues(r?.values || {}, groupList),
  }));
  if (!sanitizedRecords.length) {
    return { ok: false, message: fixedUi('请先在规格供应中选择尺码，再填写尺寸信息') };
  }
  if (sanitizedRecords.some((r) => !String(r.values[groupId] || '').trim())) {
    return { ok: false, message: fixedUi('尺码表存在空的尺码名称，请检查后再提交') };
  }

  const elements = Array.isArray(elementList) ? elementList : [];
  const complete = [];
  for (const el of elements) {
    const id = String(el?.id ?? '');
    if (!id || !(Number(el.id) > 0)) continue;
    const filledCount = sanitizedRecords.filter((r) => String(r.values[id] || '').trim()).length;
    if (filledCount === 0) continue;
    if (filledCount !== sanitizedRecords.length) {
      return {
        ok: false,
        message: `${el.name || id}${fixedUi('：请全部尺码填完，或整列留空（不可只填部分尺码）')}`,
      };
    }
    complete.push({
      id: Number(el.id),
      name: String(el.name || el.id || ''),
      ...(el?.necessary != null ? { necessary: Boolean(el.necessary) } : {}),
    });
  }

  const keepIds = new Set(complete.map((e) => String(e.id)));
  const nextRecords = sanitizedRecords.map((r) => {
    const values = {};
    Object.entries(r.values).forEach(([k, v]) => {
      if (k === groupId || keepIds.has(String(k))) values[k] = v;
    });
    return { values };
  });
  return { ok: true, elementList: complete, records: nextRecords };
}

/** sizecharts.get 单项中的尺码内容 */
export function pickOfficialTemplateContent(template = {}) {
  return template?.contentDTO
    || template?.content
    || template?.mappingContent
    || null;
}

/**
 * 平台 meta 字段兼容：实测为 groups/elements，沙箱/文档偶发 groupList/elementList。
 * 用于尺码库编辑回填（历史同步可能只落了 records、漏了 elementList）。
 */
export function normalizeOfficialSizeChartDraft(draft = null, platformRaw = null) {
  const base = draft && typeof draft === 'object' ? { ...draft } : {};
  const content = pickOfficialTemplateContent(platformRaw || {});
  const meta = content?.meta && typeof content.meta === 'object' ? content.meta : {};
  const groupList = Array.isArray(base.groupList) && base.groupList.length
    ? base.groupList
    : (Array.isArray(meta.groupList) && meta.groupList.length
      ? meta.groupList
      : (Array.isArray(meta.groups) && meta.groups.length ? meta.groups : []));
  const elementList = Array.isArray(base.elementList) && base.elementList.length
    ? base.elementList
    : (Array.isArray(meta.elementList) && meta.elementList.length
      ? meta.elementList
      : (Array.isArray(meta.elements) && meta.elements.length ? meta.elements : []));
  const records = Array.isArray(base.records) && base.records.length
    ? base.records
    : (Array.isArray(content?.records) ? content.records : []);
  // records 有值但 meta 缺列名时，用默认尺码分组，避免编辑页空等平台
  let resolvedGroups = groupList.length ? groupList : (records.length ? [{ id: 1, name: '尺码' }] : []);
  // 若落库 records 的尺码名不在当前 groupId 下，改用 records 里实际用到的分组键，避免编辑回填整表变空
  if (records.length && resolvedGroups.length) {
    const preferredId = String(resolvedGroups[0]?.id ?? 1);
    const hitPreferred = records.some((row) => {
      const values = row?.values && typeof row.values === 'object' ? row.values : {};
      return String(values[preferredId] ?? '').trim() !== '';
    });
    if (!hitPreferred) {
      const altKey = records.reduce((found, row) => {
        if (found) return found;
        const values = row?.values && typeof row.values === 'object' ? row.values : {};
        for (const key of ['1', 'sizeName', 'name']) {
          if (String(values[key] ?? '').trim()) return key;
        }
        return '';
      }, '');
      if (altKey && altKey !== preferredId) {
        resolvedGroups = [{
          id: Number.isFinite(Number(altKey)) ? Number(altKey) : altKey,
          name: resolvedGroups[0]?.name || fixedUi("尺码"),
        }];
      }
    }
  }
  if (!records.length && !elementList.length && !resolvedGroups.length && !Object.keys(base).length) {
    return null;
  }
  const rawClassId = Number(platformRaw?.classId || base?.sizeClass?.classId || 0);
  const rawClassType = Number(
    platformRaw?.classType ?? base?.sizeClass?.classType ?? 0,
  );
  const sizeClass = enrichTemuSizeChartClass(
    base.sizeClass && typeof base.sizeClass === 'object' && Number(base.sizeClass.classId) > 0
      ? base.sizeClass
      : (rawClassId > 0 ? { classId: rawClassId, classType: rawClassType } : (base.sizeClass || null)),
  );
  return {
    ...base,
    mode: base.mode || 'create',
    sizeClass,
    groupList: resolvedGroups,
    elementList,
    records,
  };
}

/**
 * 将平台尺码表库（sizecharts.get）套用到当前 meta 表格。
 * 优先按 element id 对齐，其次按名称对齐。
 * @returns {{ records: Array|{null}, name: string, filled: number, businessId: number }}
 */
export function applyOfficialSizeTemplate(template = {}, {
  groupList = [],
  elementList = [],
} = {}) {
  const businessId = Number(template?.businessId || template?.id || 0) || 0;
  const name = String(template?.name || '').trim();
  const content = pickOfficialTemplateContent(template);
  const srcRecords = Array.isArray(content?.records) ? content.records : [];
  if (!srcRecords.length) {
    return { records: null, name, filled: 0, businessId };
  }

  const srcMeta = content?.meta && typeof content.meta === 'object' ? content.meta : {};
  const srcGroups = srcMeta.groupList || srcMeta.groups || [];
  const srcElements = srcMeta.elementList || srcMeta.elements || [];
  const srcGroupId = String(srcGroups[0]?.id ?? groupList[0]?.id ?? 1);
  const dstGroupId = String(groupList[0]?.id ?? 1);

  const srcElByName = new Map();
  (srcElements || []).forEach((el) => {
    const key = String(el?.name || '').trim().toLowerCase();
    if (key && el?.id != null) srcElByName.set(key, String(el.id));
  });

  let filled = 0;
  const records = srcRecords.map((row) => {
    const srcValues = row?.values && typeof row.values === 'object' ? row.values : {};
    const values = { [dstGroupId]: '' };
    const sizeName = srcValues[srcGroupId]
      ?? srcValues[String(srcGroupId)]
      ?? srcValues['1']
      ?? '';
    values[dstGroupId] = String(sizeName || '').trim();
    if (values[dstGroupId]) filled += 1;

    (elementList || []).forEach((el) => {
      const dstId = String(el.id);
      const byId = srcValues[dstId] ?? srcValues[el.id];
      const byNameId = srcElByName.get(String(el?.name || '').trim().toLowerCase());
      const byName = byNameId != null ? (srcValues[byNameId] ?? srcValues[Number(byNameId)]) : undefined;
      const raw = byId != null && String(byId).trim() !== ''
        ? byId
        : (byName != null ? byName : '');
      values[dstId] = raw == null ? '' : String(raw);
      if (String(values[dstId] || '').trim()) filled += 1;
    });
    return { values };
  });

  return { records, name, filled, businessId };
}

/**
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateOfficialSizeChartDraft({
  required = false,
  mode = 'skip',
  sizeClass = null,
  selectedBusinessIds = [],
  records = [],
  groupList = [],
  elementList = [],
} = {}) {
  if (!required || mode === 'skip') return { ok: true };
  const need = Number(sizeClass?.classType) === 1 ? 2 : 1;
  if (mode === 'template') {
    const ids = (selectedBusinessIds || []).map((id) => Number(id)).filter((id) => id > 0);
    if (ids.length < need) {
      return {
        ok: false,
        message: need > 1
          ? `${fixedUi('套装类目请选择至少')} ${need} ${fixedUi('个尺码表模板')}`
          : fixedUi('请选择至少一个尺码表模板'),
      };
    }
    return { ok: true };
  }
  if (mode === 'create') {
    const groupId = String(groupList[0]?.id ?? 1);
    const rows = Array.isArray(records) ? records : [];
    if (!rows.length) {
      return { ok: false, message: fixedUi('请先在规格供应中选择尺码，再填写尺寸信息') };
    }
    // meta 未标记 necessary 时，表内全部测量列均视为必填（避免空值提交被 Temu 拒绝）
    const elements = Array.isArray(elementList) ? elementList : [];
    const anyNecessary = elements.some((el) => Boolean(el?.necessary));
    const requiredElements = anyNecessary
      ? elements.filter((el) => el?.necessary)
      : elements;
    for (let i = 0; i < rows.length; i += 1) {
      const values = rows[i]?.values || {};
      if (!String(values[groupId] || '').trim()) {
        return { ok: false, message: `${fixedUi('尺码表第')} ${i + 1} ${fixedUi('行请填写尺码名称')}` };
      }
      for (const el of requiredElements) {
        if (!String(values[String(el.id)] || '').trim()) {
          return { ok: false, message: `${fixedUi('尺码表第')} ${i + 1} ${fixedUi('行请填写')}「${el.name || el.id}」` };
        }
      }
    }
    // 可选列：要么整列填满，要么整列留空（平台按 meta.elementList 校验，不允许半填）
    const picked = pickFilledSizeChartElements({ records: rows, groupList, elementList: elements });
    if (!picked.ok) return picked;
    if (!picked.elementList.length) {
      return { ok: false, message: fixedUi('请至少填写一项尺寸测量值') };
    }
    return { ok: true };
  }
  return { ok: false, message: fixedUi('请完善尺码表') };
}

/**
 * 提交前解析为发品可用的 sizeTemplateIds。
 */
export async function resolveOfficialSizeTemplateIds({
  shopId,
  catId,
  required = false,
  mode = 'skip',
  sizeClass = null,
  selectedBusinessIds = [],
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  isDoubleSize = false,
  api = null,
} = {}) {
  if (!required || mode === 'skip') return [];
  if (!shopId || !api) throw new Error('缺少店铺或尺码表接口');

  if (mode === 'template') {
    const ids = (selectedBusinessIds || []).map((id) => Number(id)).filter((id) => id > 0);
    if (!ids.length) throw new Error('请选择至少一个尺码表模板');
    const temps = [];
    for (const businessId of ids) {
      const res = await api.createPublishSizeChartTemp(shopId, businessId);
      const tempId = Number(res?.tempBusinessId || 0);
      if (!tempId) throw new Error(`模板 ${businessId} 未返回 tempBusinessId`);
      temps.push(tempId);
    }
    return temps;
  }

  const payload = buildOfficialSizeChartCreatePayload({
    catId,
    sizeClass,
    chartName,
    records,
    groupList,
    elementList,
    reusable: false,
    isDoubleSize,
  });
  let created;
  try {
    created = await api.createPublishSizeChart(shopId, payload);
  } catch (e) {
    const raw = String(e?.message || e?.userMessage || e || '').trim();
    if (/empty size value/i.test(raw) || /空的?尺码|尺码.*空/.test(raw)) {
      throw new Error('尺码表存在未填写的尺寸值：带 * 的必填；其它列请整列填完或整列留空后再提交');
    }
    if (/保存尺码数据失败|100021|数据错误|100001/i.test(raw)) {
      throw new Error(
        '平台拒绝保存尺码表：请确认带 * 列已填，且肩宽/袖长等可选列不要只填一部分（空列会自动不传）',
      );
    }
    throw e;
  }
  const businessId = Number(created?.businessId || 0);
  if (!businessId) throw new Error('创建尺码表未返回 businessId');
  return [businessId];
}

/** 组装官方尺码表 create 入参（可复用模板 / 一次性表共用） */
export function buildOfficialSizeChartCreatePayload({
  catId,
  sizeClass = null,
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  reusable = false,
  isDoubleSize = false,
} = {}) {
  const classId = Number(sizeClass?.classId || 0);
  const classType = Number(sizeClass?.classType || 0);
  if (!(classId > 0)) throw new Error('缺少尺码分类 classId');
  const groupIdNum = Number(groupList[0]?.id ?? 1) || 1;
  const picked = pickFilledSizeChartElements({ records, groupList, elementList });
  if (!picked.ok) throw new Error(picked.message);
  if (!picked.elementList.length) {
    throw new Error('请至少填写一项尺寸测量值');
  }
  const payload = {
    classId,
    name: String(chartName || '发品尺码表').trim() || '发品尺码表',
    reusable: Boolean(reusable),
    content: {
      // 官方样例：generalSizeType 与尺码组 id 一致
      generalSizeType: groupIdNum,
      localSizeSource: 1,
      meta: {
        groupList: (groupList || []).map((g) => ({
          id: Number(g.id) || groupIdNum,
          name: String(g.name || fixedUi("尺码")),
        })),
        // 只声明有完整数值的测量列（与官方样例一致）
        elementList: picked.elementList.map((e) => ({
          id: Number(e.id),
          name: String(e.name || e.id || ''),
        })),
      },
      records: picked.records,
    },
  };
  if (isDoubleSize) {
    payload.isDoubleSize = true;
  }
  if (classType !== 1 && Number(catId) > 0) {
    payload.catId = Number(catId);
  }
  return payload;
}

/**
 * 将当前填写的尺寸保存为平台可复用尺码模板（reusable=true）。
 * @returns {Promise<{ businessId: number, name: string }>}
 */
export async function saveOfficialSizeChartAsReusableTemplate({
  shopId,
  catId,
  sizeClass = null,
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  isDoubleSize = false,
  api = null,
} = {}) {
  if (!shopId || !api) throw new Error('缺少店铺或尺码表接口');
  const check = validateOfficialSizeChartDraft({
    required: true,
    mode: 'create',
    sizeClass,
    records,
    groupList,
    elementList,
  });
  if (!check.ok) throw new Error(check.message);
  const payload = buildOfficialSizeChartCreatePayload({
    catId,
    sizeClass,
    chartName,
    records,
    groupList,
    elementList,
    reusable: true,
    isDoubleSize,
  });
  const created = await api.createPublishSizeChart(shopId, payload);
  const businessId = Number(created?.businessId || 0);
  if (!businessId) throw new Error('保存尺码模板未返回 businessId');
  return { businessId, name: payload.name };
}

/**
 * 将本地尺码库记录提交到 Temu 平台（sizecharts.create），并组装可回写的 IPE payload。
 * 若 payload 已带 platformBusinessIds，则只规范化回写，不重复创建。
 *
 * @returns {Promise<{ businessId: number, payload: object, alreadySynced: boolean }>}
 */
export async function submitLocalOfficialSizeChartToPlatform({
  shopId,
  item = {},
  api = null,
} = {}) {
  if (!shopId || !api) throw new Error('缺少店铺或尺码表接口');
  const rawPayload = item?.payload && typeof item.payload === 'object' ? item.payload : {};
  if (!isOfficialSizeChartTemplatePayload(rawPayload)) {
    throw new Error('该模板不是官方尺码表格式，无法提交到平台');
  }

  const existingIds = (Array.isArray(rawPayload.platformBusinessIds) ? rawPayload.platformBusinessIds : [])
    .map(Number)
    .filter((id) => id > 0);
  const draft = normalizeOfficialSizeChartDraft(
    rawPayload.officialSizeChart,
    rawPayload.platformRaw,
  );
  const draftIds = (Array.isArray(draft?.selectedBusinessIds) ? draft.selectedBusinessIds : [])
    .map(Number)
    .filter((id) => id > 0);
  let businessId = existingIds[0] || draftIds[0] || 0;
  let alreadySynced = businessId > 0;

  if (!businessId) {
    const sizeClass = draft?.sizeClass
      || (rawPayload.sizeClass && typeof rawPayload.sizeClass === 'object' ? rawPayload.sizeClass : null);
    const records = Array.isArray(draft?.records) ? draft.records : [];
    const groupList = Array.isArray(draft?.groupList) ? draft.groupList : [];
    const elementList = Array.isArray(draft?.elementList) ? draft.elementList : [];
    const catId = String(
      rawPayload.categoryId
      || item.categoryId
      || rawPayload.productTypeId
      || item.productTypeId
      || '',
    ).trim();
    const saved = await saveOfficialSizeChartAsReusableTemplate({
      shopId,
      catId,
      sizeClass,
      chartName: String(item.name || rawPayload.name || '尺码表').trim() || '尺码表',
      records,
      groupList,
      elementList,
      isDoubleSize: Boolean(draft?.isDoubleSize || rawPayload.isDoubleSize),
      api,
    });
    businessId = Number(saved?.businessId || 0);
    alreadySynced = false;
  }

  if (!businessId) throw new Error('提交平台未返回 businessId');

  const nextDraft = {
    ...(draft && typeof draft === 'object' ? draft : {}),
    mode: 'template',
    selectedBusinessIds: [businessId],
    selectedLibraryId: String(businessId),
  };
  const payload = buildOfficialSizeChartTemplatePayload({
    categoryId: rawPayload.categoryId || item.categoryId || '',
    productTypeId: rawPayload.productTypeId || item.productTypeId || '',
    categoryPathLabel: rawPayload.categoryPathLabel || item.categoryPathLabel || '',
    selectedSizeLabels: rawPayload.selectedSizeLabels || [],
    selectedSizeIds: rawPayload.selectedSizeIds || [],
    sizeClass: nextDraft.sizeClass || rawPayload.sizeClass || null,
    officialSizeChart: nextDraft,
    platformBusinessIds: [businessId],
  });
  payload.source = 'platform';
  return { businessId, payload, alreadySynced };
}

/** IPE 尺码库是否为官方尺码表语义 */
export function isOfficialSizeChartTemplatePayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.kind === 'temu-official') return true;
  if (payload.officialSizeChart && typeof payload.officialSizeChart === 'object') return true;
  return false;
}

/** 组装 IPE 尺码库 payload（官方尺码表语义，可被产品模板 / 发品草稿回填） */
export function buildOfficialSizeChartTemplatePayload({
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  selectedSizeLabels = [],
  selectedSizeIds = [],
  sizeClass = null,
  officialSizeChart = null,
  platformBusinessIds = [],
} = {}) {
  const labels = (Array.isArray(selectedSizeLabels) ? selectedSizeLabels : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  const businessIds = (Array.isArray(platformBusinessIds) ? platformBusinessIds : [])
    .map((id) => Number(id))
    .filter((id) => id > 0);
  return {
    kind: 'temu-official',
    categoryId: categoryId ? String(categoryId) : '',
    productTypeId: productTypeId ? String(productTypeId) : '',
    categoryPathLabel: categoryPathLabel ? String(categoryPathLabel) : '',
    selectedSizeLabels: labels,
    selectedSizeIds: (Array.isArray(selectedSizeIds) ? selectedSizeIds : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean),
    sizeClass: sizeClass && typeof sizeClass === 'object' ? sizeClass : null,
    officialSizeChart: officialSizeChart && typeof officialSizeChart === 'object'
      ? officialSizeChart
      : null,
    isDoubleSize: Boolean(officialSizeChart?.isDoubleSize),
    platformBusinessIds: businessIds,
    // 兼容旧字段：不再写 SHEIN 矩阵 sizeChart
    sizeChart: {},
  };
}

/**
 * 从类目尺码属性 + 已选尺码构造模特试穿尺码选项。
 */
export function buildTemuModelTryOnSizeOptions({
  sizeAttr = null,
  attrTpl = null,
  selectedSizeIds = [],
  skcList = [],
} = {}) {
  if (!sizeAttr) return [];
  const selected = new Set();
  (Array.isArray(selectedSizeIds) ? selectedSizeIds : []).forEach((id) => {
    const key = String(id || '').trim();
    if (key) selected.add(key);
  });
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = String(sku?.sizeValueId || '').trim();
      if (key) selected.add(key);
    });
  });
  if (!selected.size) return [];

  const rawInfos = attrTpl?.raw?.data?.[0]?.attribute_infos
    || attrTpl?.raw?.attribute_infos
    || [];
  const raw = (Array.isArray(rawInfos) ? rawInfos : []).find(
    (r) => String(r?.attribute_id ?? r?.attributeId) === String(sizeAttr.attributeId || ''),
  );
  const rawValues = Array.isArray(raw?.attribute_value_info_list)
    ? raw.attribute_value_info_list
    : [];

  return (sizeAttr.values || [])
    .filter((v) => selected.has(String(v.id)))
    .map((v) => {
      const rawVal = rawValues.find(
        (row) => String(row?.attribute_value_id ?? row?.attributeValueId) === String(v.id),
      );
      const specId = Number(rawVal?.temu_spec_id ?? rawVal?.temuSpecId ?? v.id) || 0;
      const name = String(v.name || v.nameCn || '').trim();
      if (!name || !(specId > 0)) return null;
      return { id: String(v.id), name, specId };
    })
    .filter(Boolean);
}

/** 模特模板 / 平台同步模特 → goodsModelReqs 单项 */
export function normalizeTemuTryOnSizeName(name = '') {
  return String(name || '')
    .trim()
    .replace(/码$/u, '')
    .replace(/\s+/g, '')
    .toUpperCase();
}

/** 平台拒「均码 / Free Size」等非商品规格尺码 */
export function isIllegalTemuTryOnSizeName(name = '') {
  const raw = String(name || '').trim();
  if (!raw) return true;
  const n = normalizeTemuTryOnSizeName(raw);
  return /^(均码|ONESIZE|ONSIZE|FREESIZE|OS|FREE)$/i.test(n)
    || /^均码$/u.test(raw);
}

/** tryOnResult：TRUE_TO_SIZE(1)/TOO_SMALL(2)/TOO_LARGE(3)，文档可选，默认合身 */
export const TEMU_TRY_ON_RESULT_TRUE_TO_SIZE = 1;
export const TEMU_TRY_ON_RESULT_OPTIONS = [
  { value: 1, label: '合身' },
  { value: 2, label: '紧身' },
  { value: 3, label: '宽松' },
];

export function normalizeTemuTryOnResult(value, fallback = TEMU_TRY_ON_RESULT_TRUE_TO_SIZE) {
  const n = Number(value);
  if (n === 1 || n === 2 || n === 3) return n;
  return fallback;
}

/**
 * 从商品已选尺码解析试穿尺码（goodsModelReqs.sizeSpecId/Name 必须是商品规格，不是模特库字段）。
 * @param {Array<{ id?: string, name?: string, specId?: number|string }>} sizeOptions
 */
export function resolveGoodsModelTryOnSize(preferredName = '', sizeOptions = []) {
  const options = (Array.isArray(sizeOptions) ? sizeOptions : [])
    .map((opt) => ({
      id: String(opt?.id ?? '').trim(),
      name: String(opt?.name || '').trim(),
      specId: Number(opt?.specId || opt?.id || 0) || 0,
    }))
    .filter((opt) => opt.name && opt.specId > 0);
  if (!options.length) return null;

  const preferredKey = normalizeTemuTryOnSizeName(preferredName);
  const hit = preferredKey
    ? options.find((opt) => normalizeTemuTryOnSizeName(opt.name) === preferredKey)
    : null;
  const picked = hit || (options.length === 1 ? options[0] : null);
  if (!picked) return null;
  return {
    sizeSpecId: picked.specId,
    sizeSpecName: picked.name,
    sizeValueId: picked.id,
  };
}

export function mapModelTemplateToGoodsModelReq(item = {}, sizeOptions = []) {
  const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
  const clothes = payload.clothesModel || item.clothesModel || {};
  const shoe = payload.shoeModel || item.shoeModel || {};
  const modelName = String(
    payload.modelName || item.modelName || item.name || '',
  ).trim();
  const portraitCandidates = [
    item.thumbUrl,
    payload.thumbUrl,
    payload.modelProfileUrl,
    item.modelProfileUrl,
    payload.headPortrait,
    item.headPortrait,
    payload.avatarUrl,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const modelProfileUrl = portraitCandidates.find((url) => /^https?:\/\//i.test(url))
    || portraitCandidates[0]
    || '';
  const portraitIpe = portraitCandidates.find((url) => url.startsWith('/uploads/')) || '';
  const modelId = Number(
    payload.modelId ?? item.modelId ?? item.id ?? 0,
  ) || undefined;
  const preferredSizeName = String(
    payload.sizeSpecName
    || item.sizeSpecName
    || payload.tryOnSize
    || item.tryOnSize
    || '',
  ).trim();
  const resolvedSize = resolveGoodsModelTryOnSize(preferredSizeName, sizeOptions);
  // 禁止默认「均码」：平台会拒「模特试穿尺码不合法：均码」
  const sizeSpecId = resolvedSize?.sizeSpecId
    || Number(payload.sizeSpecId ?? item.sizeSpecId ?? 0)
    || 0;
  const sizeSpecName = resolvedSize?.sizeSpecName
    || (!isIllegalTemuTryOnSizeName(preferredSizeName) ? preferredSizeName : '');
  const platformModelType = payload.modelType ?? item.modelType;
  // 平台 modelinfo：0 成衣 / 1 鞋模；发品默认成衣=1
  const modelType = platformModelType === 0 || platformModelType === 1
    ? Number(platformModelType) + 1
    : (Number(platformModelType ?? item.modelType ?? 1) || 1);
  const req = {
    modelName: modelName || fixedUi("模特"),
    modelProfileUrl: modelProfileUrl || 'https://img.kwcdn.com/placeholder/model.png',
    modelType,
    modelFeature: Number(payload.modelFeature ?? item.modelFeature ?? 1) || 1,
  };
  if (sizeSpecId > 0) req.sizeSpecId = sizeSpecId;
  if (sizeSpecName) req.sizeSpecName = sizeSpecName;
  if (resolvedSize?.sizeValueId) req.sizeValueId = resolvedSize.sizeValueId;
  if (modelId) req.modelId = modelId;
  const templateRowId = String(item.id || item.templateId || '').trim();
  if (templateRowId) req._templateId = templateRowId;
  if (modelName) req._templateName = modelName;
  if (portraitIpe) req._portraitIpe = portraitIpe;
  const measure = {
    modelHeight: payload.modelHeight ?? item.height ?? clothes.height,
    modelBust: payload.modelBust ?? item.bust ?? clothes.bust,
    modelWaist: payload.modelWaist ?? item.waist ?? clothes.waist,
    modelHip: payload.modelHip ?? item.hip ?? clothes.hipline,
    modelFootLength: payload.modelFootLength ?? item.footLength ?? shoe.footLength,
    modelFootWidth: payload.modelFootWidth ?? item.footWidth ?? shoe.footWidth,
    sizeTemplateId: payload.sizeTemplateId ?? item.sizeTemplateId,
  };
  Object.entries(measure).forEach(([key, val]) => {
    if (val != null && String(val).trim() !== '') req[key] = val;
  });
  req.tryOnResult = normalizeTemuTryOnResult(payload.tryOnResult ?? item.tryOnResult);
  return req;
}
